import type { ErrorEvent } from '@sentry/react';
import type { Policy } from './policy';

/**
 * In-memory set of Sentry fingerprints we've already let through during this
 * tab session. Used to guarantee that the first event for a previously
 * unseen fingerprint always reaches Sentry, even for sampled categories.
 *
 * Cardinality is bounded by the variety of fingerprints the app produces in
 * a single session, so no eviction is needed.
 */
const seenFingerprints = new Set<string>();

const fingerprintKey = (event: ErrorEvent): string =>
  (event.fingerprint ?? []).join('|');

/**
 * Decide whether `event` should be sent given `policy`.
 *
 * - `drop` → always false
 * - `pass` → always true
 * - `sample` → true if first occurrence of fingerprint in this session,
 *   otherwise probabilistic at `policy.rate`
 *
 * `random` is injected for deterministic testing.
 */
export const shouldSendEvent = (
  event: ErrorEvent,
  policy: Policy,
  random: () => number = Math.random
): boolean => {
  if (policy.type === 'drop') return false;
  if (policy.type === 'pass') return true;

  const key = fingerprintKey(event);

  if (key && !seenFingerprints.has(key)) {
    seenFingerprints.add(key);
    return true;
  }

  return random() < policy.rate;
};

/** Test-only helper. Not exported via index. */
export const __resetSeenFingerprintsForTests = () => {
  seenFingerprints.clear();
};
